import type { SortingState } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { parseAsBoolean, parseAsString, useQueryState } from '@frontend/utils/hooks/useQueryState';

/**
 * Hook to manage sorting state in URL search parameters
 *
 * @returns Sorting state and setter compatible with TanStack Table
 */
export function useSortingSearchParams(): [
    sorting: SortingState,
    setSorting: (updater: SortingState | ((state: SortingState) => SortingState)) => void,
] {
    const defaultField = 'expectedDateCompleted';
    const defaultDesc = false;

    const [sortField, setSortField] = useQueryState('sort', parseAsString.withDefault(defaultField));
    const [sortDesc, setSortDesc] = useQueryState('desc', parseAsBoolean.withDefault(defaultDesc));

    const sorting: SortingState = useMemo(
        () => [
            {
                desc: sortDesc,
                id: sortField,
            },
        ],
        [sortField, sortDesc]
    );

    const setSorting = useCallback(
        (updater: SortingState | ((state: SortingState) => SortingState)) => {
            const newSorting = typeof updater === 'function' ? updater(sorting) : updater;

            if (newSorting.length === 0) {
                setSortField(defaultField);
                setSortDesc(defaultDesc);
            } else {
                const firstSortItem = newSorting[0];
                setSortField(firstSortItem.id);
                setSortDesc(firstSortItem.desc);
            }
        },
        [sorting, setSortField, setSortDesc, defaultField, defaultDesc]
    );

    return [sorting, setSorting];
}
